// Whether the person in front of the app is in the middle of something (issue #3265).
//
// Only one update needs to know: reloading the page onto a new bundle. A reload throws away
// whatever has not been committed to the server yet, and the two places that hurt are a Liturgy
// Planner cell (committed only on Enter or blur) and a song upload (a reload aborts the request, or
// skips the bookkeeping that follows it). Reloading a song's files into memory disturbs neither, so
// song reloads stay on the playback gate alone.
//
// Two inputs, because the two problems are different shapes:
//
//   - Ambient activity. Anything the user does marks the clock, and the page reload waits until
//     they have been quiet for userIdleMs. This is a short settle time, not an idle policy: it only
//     keeps a reload from landing in the middle of a tap or a swipe. A long window would starve the
//     in-session reload for the common "open, play a few songs, close" session (the next launch
//     runs the new bundle regardless, since the waiting service worker activates once the old one
//     has no open pages), and the cases where a reload destroys work are covered by holds instead.
//   - An explicit hold. A component that knows it has state worth keeping (an open dialog, a cell
//     being edited, an upload in flight) takes a hold for as long as that is true, so the reload
//     waits however long the work takes, even if the user sits still while it runs.
//
// pointerdown and keydown are the activity, deliberately: they are what typing and clicking
// produce. pointermove is not, because a mouse crossing the window would otherwise hold a reload
// off forever.
//
// A React-free store, in the shape of playback_state.ts, so update_gate.ts can read it without
// taking a React dependency, and so the hold can be taken from anywhere.

export const userIdleMs = 5_000;

let lastUserActivityAt: number | undefined;
let holdCount = 0;
const holdListeners = new Set<() => void>();
let areListenersRegistered = false;

// `now` is injectable for the tests; the listeners below leave it to Date.now().
export function noteUserActivity(now = Date.now()): void {
  lastUserActivityAt = now;
}

// undefined until the user first does something: a page nobody has touched is idle, which is the
// right answer for a tab left open on the projector.
export function getLastUserActivityAt(): number | undefined {
  return lastUserActivityAt;
}

// Counted, so nested and overlapping holds (a dialog inside an editor) each release only their own.
// The returned release is idempotent: React may run an effect's cleanup once and only once, but a
// double call must not lift somebody else's hold.
export function holdAppReload(): () => void {
  holdCount += 1;
  notifyHoldListeners();
  let isReleased = false;
  return () => {
    if (isReleased) {
      return;
    }
    isReleased = true;
    holdCount -= 1;
    notifyHoldListeners();
  };
}

export function isAppReloadHeld(): boolean {
  return holdCount > 0;
}

// Whether the user has a text field focused. A field commits its value on blur, so a reload while
// one is focused discards whatever is typed in it - the loss that the explicit holds exist to
// prevent, covered here for every field at once. Buttons, checkboxes and the like hold no draft.
export function isEditableElementFocused(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const element = document.activeElement;
  if (!element || element === document.body) {
    return false;
  }
  if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
    return true;
  }
  if (element instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'file', 'color', 'image'].includes(
      element.type,
    );
  }
  return (element as HTMLElement).isContentEditable === true;
}

// Fires whenever the user gate's inputs change: the hold count, or focus moving into or out of a
// field. The gate cares about the opening - a reload that has been waiting for an upload must go the
// moment the upload is done, not userIdleMs later.
export function subscribeAppReloadHolds(listener: () => void): () => void {
  holdListeners.add(listener);
  return () => {
    holdListeners.delete(listener);
  };
}

function notifyHoldListeners(): void {
  // copy the set so a listener that unsubscribes itself does not disturb the iteration
  for (const listener of [...holdListeners]) {
    listener();
  }
}

function onUserActivity(): void {
  noteUserActivity();
}

// Called once from initializeApp. Idempotent, because a second call would double-count nothing but
// would leave a listener nobody can remove.
export function registerUserActivityListeners(): void {
  if (areListenersRegistered) {
    return;
  }
  areListenersRegistered = true;
  // passive: this never calls preventDefault, and the app's keyboard and pointer handling must not
  // wait on it
  document.addEventListener('pointerdown', onUserActivity, { passive: true });
  document.addEventListener('keydown', onUserActivity, { passive: true });
  // focus moving between fields: focusout fires before the next element is focused, so the check
  // waits a tick rather than reading a momentarily empty activeElement as "nothing focused"
  document.addEventListener('focusin', notifyHoldListeners, { passive: true });
  document.addEventListener('focusout', () => setTimeout(notifyHoldListeners, 0), { passive: true });
}

// test-only: the store is module state, so tests must be able to put it back
export function resetUserActivityForTests(): void {
  lastUserActivityAt = undefined;
  holdCount = 0;
  holdListeners.clear();
  if (areListenersRegistered) {
    document.removeEventListener('pointerdown', onUserActivity);
    document.removeEventListener('keydown', onUserActivity);
    areListenersRegistered = false;
  }
}
